import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { cn } from '@/lib/utils';
import type { ApiController } from '@/types/api';

const schema = z.object({
  name: z.string().min(1, 'El nombre es requerido').max(100),
  serialNumber: z
    .string()
    .min(1, 'El número de serie es requerido')
    .regex(/^SWAT_/i, 'Formato esperado: SWAT_XXXXXXXX'),
  companyId: z.string().optional(),
  lat: z.coerce.number().optional(),
  lng: z.coerce.number().optional(),
});

type FormData = z.infer<typeof schema>;

interface SwatidControllerFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  controller?: ApiController | null;
  companies?: { id: string; name: string }[];
  onSubmit: (data: FormData) => void;
  isLoading?: boolean;
  userRole?: 'superadmin' | 'company_admin';
}

function NativeSelect({
  value,
  onChange,
  placeholder,
  options,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  options: { value: string; label: string }[];
  disabled?: boolean;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className={cn(
        'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm',
        disabled && 'opacity-50 cursor-not-allowed'
      )}
    >
      {placeholder && (
        <option value="" disabled>
          {placeholder}
        </option>
      )}
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

export function SwatidControllerFormDialog({
  open,
  onOpenChange,
  controller,
  companies = [],
  onSubmit,
  isLoading = false,
  userRole = 'company_admin',
}: SwatidControllerFormDialogProps) {
  const isEditing = !!controller;
  const isSuperadmin = userRole === 'superadmin';

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      serialNumber: '',
      companyId: '',
      lat: undefined,
      lng: undefined,
    },
  });

  useEffect(() => {
    if (!open) return;
    if (controller) {
      form.reset({
        name: controller.name,
        serialNumber: controller.serialNumber || '',
        companyId: controller.companyId || '',
        lat: controller.coordinates?.lat,
        lng: controller.coordinates?.lng,
      });
    } else {
      form.reset({
        name: '',
        serialNumber: '',
        companyId: '',
        lat: undefined,
        lng: undefined,
      });
    }
  }, [open, controller, form]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Editar controladora SWATID' : 'Nueva controladora SWATID-16'}
          </DialogTitle>
          <DialogDescription>
            Identificación por número de serie. Comunicación MQTT directa (16 relés).
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nombre</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="SWATID Jardín Norte" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="serialNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Número de serie</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="SWAT_02EDA02B4D20"
                      disabled={isEditing && !isSuperadmin}
                      className="font-mono"
                    />
                  </FormControl>
                  <FormDescription>Clave única del dispositivo</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {isSuperadmin && (
              <FormField
                control={form.control}
                name="companyId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Empresa</FormLabel>
                    <FormControl>
                      <NativeSelect
                        value={field.value || ''}
                        onChange={field.onChange}
                        placeholder="Sin asignar"
                        options={[
                          { value: '', label: 'Sin asignar' },
                          ...companies.map((c) => ({ value: c.id, label: c.name })),
                        ]}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name="lat"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Latitud</FormLabel>
                    <FormControl>
                      <Input type="number" step="any" {...field} value={field.value ?? ''} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lng"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Longitud</FormLabel>
                    <FormControl>
                      <Input type="number" step="any" {...field} value={field.value ?? ''} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? 'Guardando...' : isEditing ? 'Actualizar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
